// frontend/src/components/common/Sidebar.js

import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import UserAvatar from "./UserAvatar";

function Sidebar({ isOpen }) {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = String(user?.role || "").trim() === "Admin";
  const isManagerOrOwner = [
    "Admin", "SM", "Sales Owner", "Sales Manager",
  ].includes(String(user?.role || "").trim());

  const navItems = [
    { path: "/dashboard", icon: "fa-th-large", label: "Dashboard" },
    { path: "/leads", icon: "fa-users", label: "My Leads" },
    { path: "/leads/add", icon: "fa-plus-circle", label: "New Lead" },
    { path: "/reports", icon: "fa-file-alt", label: "Reports" },
    { path: "/profile", icon: "fa-user-circle", label: "My Profile" },
  ];

  const adminItems = [
    { path: "/admin/users", icon: "fa-user-cog", label: "Users" },
    { path: "/admin/dms", icon: "fa-server", label: "DMS Providers" },
    { path: "/admin/products", icon: "fa-boxes", label: "Products" },
    { path: "/admin/master", icon: "fa-database", label: "Master Data" },
    { path: "/admin/booking-apps", icon: "fa-calendar-check", label: "Booking Apps" },
  ];

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    if (path === "/leads/add") return location.pathname === "/leads/add";
    if (path === "/leads") {
      if (location.pathname === "/leads/add") return false;
      if (/^\/leads\/\d+/.test(location.pathname)) return false;
      return location.pathname === "/leads" || location.pathname === "/leads/";
    }
    if (path.startsWith("/admin/")) {
      return location.pathname === path || location.pathname.startsWith(path);
    }
    return location.pathname === path;
  };

  return (
    <aside className={`sidebar ${isOpen ? "open" : "collapsed"}`}>
      <div
        className="sidebar-brand"
        onClick={() => (window.location.href = "/dashboard")}
        role="button"
        tabIndex={0}
      >
        <div className="brand-lockup">
          <span className="brand-word">IVSG</span>
          <span className="brand-dot" />
        </div>
        <span className="brand-tagline">Smarter Tech. Smarter Customers™</span>
        <span className="portal-label">CRM PORTAL</span>
      </div>

      <div className="sidebar-divider" />

      <nav className="sidebar-nav">
        <div className="nav-section">
          <div className="nav-section-title">Workspace</div>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={`nav-item ${isActive(item.path) ? "active" : ""}`}
              end={
                item.path === "/dashboard" ||
                item.path === "/leads" ||
                item.path === "/leads/add" ||
                item.path === "/reports"
              }
            >
              <span className="nav-icon">
                <i className={`fas ${item.icon}`} />
              </span>
              <span className="nav-label">{item.label}</span>
            </NavLink>
          ))}
        </div>

        {isManagerOrOwner && (
          <div className="nav-section admin-section">
            <div className="nav-section-title">
              {isAdmin ? "Administration" : "Manager"}
            </div>

            {isAdmin &&
              adminItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isActive(item.path) ? "active" : ""}`}
                >
                  <span className="nav-icon">
                    <i className={`fas ${item.icon}`} />
                  </span>
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              ))}

            <NavLink
              to="/analytics"
              className={({ isActive }) =>
                `nav-item ${isActive ? "active" : ""}`
              }
            >
              <span className="nav-icon">
                <i className="fas fa-chart-line" />
              </span>
              <span className="nav-label">Analytics</span>
            </NavLink>

            {isAdmin && (
              <NavLink
                to="/developer"
                className={({ isActive }) =>
                  `nav-item ${isActive ? "active" : ""}`
                }
              >
                <span className="nav-icon">
                  <i className="fas fa-code" />
                </span>
                <span className="nav-label">Developer Portal</span>
              </NavLink>
            )}
          </div>
        )}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user">
          <UserAvatar
            name={user?.fullName}
            src={user?.profileImageUrl}
            size="md"
          />
          <div className="user-info">
            <strong>{user?.fullName || "User"}</strong>
            <span>{user?.role || "SR"}</span>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;